import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Text } from '@chakra-ui/core';
import { apiV1 } from '../../../../common/api/api';
import { getUploadError } from '../../../../common/helpers/fileUpload';
import { FundingDocumentsList } from '../../../../common/components/Funders/FundingDocumentsList';
import {
  uploadFundingDocumentFiles,
  uploadProgressMessage,
  uploadSummaryMessage,
} from '../../../../common/components/Funders/fundingDocumentUpload';
import { FormSection } from '../../../../common/components/Form/Form';

const SECTION_ANCHORS = {
  administrative: 'requiredFundingDocuments',
  financing: 'anahFundingDocuments',
  project_end: 'projectEndFundingDocuments',
};

export const FundingDocumentsReviewPanel = ({
  folderId,
  onReviewed,
  onChanged,
  section = 'administrative',
  onlyKey,
  title = 'Pièces administratives',
  intro = 'Déposez, consultez et validez ici les pièces administratives du dossier.',
  access,
  plain = false,
}) => {
  const authentication = useSelector(state => state.authentication || {});
  const currentUser = authentication.profile?.role ? authentication.profile : authentication.user;
  const role = currentUser?.role;
  // `access` lets a screen opened to companies show the panel; the API still enforces every right.
  const canRead =
    Boolean(access) || ['administrateur', 'conseiller social', 'ergothérapeute'].includes(role);
  const canOperate =
    ['administrateur', 'conseiller social'].includes(role) ||
    (role === 'ergothérapeute' && currentUser?.ergoQualification === 'ergothérapeute interne');
  const canUpload = access ? Boolean(access.canUpload) : canOperate;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const requestScope = useRef(0);
  const loadDocuments = () =>
    section === 'administrative'
      ? apiV1.get(`/folders/${folderId}/funding_documents`)
      : apiV1.get(`/folders/${folderId}/funding_documents`, { params: { section } });
  const [documents, setDocuments] = useState([]);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState(false);
  const [reviewPendingId, setReviewPendingId] = useState(null);
  const [reviewError, setReviewError] = useState(false);
  const [uploadPendingKey, setUploadPendingKey] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    if (!folderId || !canRead) return undefined;
    let active = true;
    requestScope.current += 1;
    setReviewPendingId(null);
    setReviewError(false);
    setUploadPendingKey(null);
    setUploadStatus(null);
    setUploadError(null);
    setPending(true);
    setError(false);
    loadDocuments()
      .then(({ data }) => {
        if (active) setDocuments(data.documents || []);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setPending(false);
      });
    return () => {
      active = false;
      requestScope.current += 1;
    };
  }, [folderId, canRead, section]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onChangedRef.current && !pending && !error) onChangedRef.current(documents);
  }, [documents, pending, error]);

  if (!folderId || !canRead) return null;

  const removeFile = (document, file) => {
    if (uploadPendingKey || reviewPendingId) return;
    const scope = requestScope.current;
    setReviewPendingId(document.id);
    setReviewError(false);
    apiV1
      .delete(`/folders/${folderId}/funding_documents/${document.id}/remove_file`, {
        data: { fundingDocument: { sharepointPath: file.sharepointPath } },
      })
      .then(({ data }) => {
        if (scope !== requestScope.current) return;
        setDocuments(current => current.map(item => (item.id === data.id ? data : item)));
      })
      .catch(removalFailure => {
        if (scope !== requestScope.current) return;
        setReviewError(
          removalFailure?.response?.data?.errors
            ? getUploadError(removalFailure)
            : 'Le fichier n’a pas pu être supprimé. Vous pouvez réessayer.',
        );
      })
      .finally(() => {
        if (scope === requestScope.current) setReviewPendingId(null);
      });
  };

  const uploadDocument = async (key, files) => {
    if (!files.length || uploadPendingKey || reviewPendingId) return;
    const scope = requestScope.current;
    setUploadPendingKey(key);
    setUploadError(null);
    try {
      const { uploaded, failures } = await uploadFundingDocumentFiles({
        folderId,
        key,
        files,
        onProgress: (position, total) => {
          if (scope === requestScope.current)
            setUploadStatus({ key, message: uploadProgressMessage(position, total) });
        },
      });
      if (scope !== requestScope.current) return;
      if (uploaded) {
        const { data } = await loadDocuments();
        if (scope !== requestScope.current) return;
        setDocuments(data.documents || []);
      }
      setUploadStatus({ key, message: uploadSummaryMessage(uploaded, files.length) });
      if (failures.length) setUploadError({ key, message: failures.join('\n') });
    } catch {
      if (scope === requestScope.current) setError(true);
    } finally {
      if (scope === requestScope.current) setUploadPendingKey(null);
    }
  };

  const reviewDocument = (document, state, reviewComment) => {
    const scope = requestScope.current;
    setReviewPendingId(document.id);
    setReviewError(false);
    apiV1
      .patch(`/folders/${folderId}/funding_documents/${document.id}`, {
        fundingDocument: {
          state,
          reviewVersion: document.reviewVersion,
          reviewComment: reviewComment || undefined,
        },
      })
      .then(({ data }) => {
        if (scope !== requestScope.current) return;
        setDocuments(current => current.map(item => (item.id === data.id ? data : item)));
        if (onReviewed) onReviewed(data);
      })
      .catch(async reviewFailure => {
        if (scope !== requestScope.current) return;
        setReviewError(
          reviewFailure?.response?.data?.errors
            ? getUploadError(reviewFailure)
            : 'La validation n’a pas pu être enregistrée. Vous pouvez réessayer.',
        );
        try {
          const { data } = await loadDocuments();
          if (scope === requestScope.current) setDocuments(data.documents || []);
        } catch {
          if (scope === requestScope.current) setError(true);
        }
      })
      .finally(() => {
        if (scope === requestScope.current) setReviewPendingId(null);
      });
  };

  const shownDocuments = onlyKey
    ? documents.filter(document => document.key === onlyKey)
    : documents;
  const pendingCount = shownDocuments.filter(document => document.state === 'under_review').length;

  const content = (
    <>
      <Text mb={4} color="semiDarkGray.600">
        {pendingCount > 0
          ? `${pendingCount} pièce${
              pendingCount > 1 ? 's' : ''
            } à valider par BelAvie. Une tâche est transmise au PSA du dossier.`
          : intro}
      </Text>
      {reviewError && (
        <Text role="alert" color="red.500" mb={3}>
          {reviewError}
        </Text>
      )}
      <FundingDocumentsList
        folderId={folderId}
        documents={shownDocuments}
        pending={pending}
        error={error}
        canReview={canOperate}
        canUpload={canUpload}
        onUpload={uploadDocument}
        onRemoveFile={removeFile}
        uploadPendingKey={uploadPendingKey}
        uploadStatus={uploadStatus}
        uploadError={uploadError}
        onReview={reviewDocument}
        reviewPendingId={reviewPendingId}
      />
    </>
  );

  if (plain) {
    return (
      <Box>
        <Text fontWeight="semibold" mb={1}>
          {title}
        </Text>
        {content}
      </Box>
    );
  }

  return (
    <Box id={onlyKey ? undefined : SECTION_ANCHORS[section]} mb={8}>
      <FormSection title={title}>{content}</FormSection>
    </Box>
  );
};
